import { useState } from "react";
import { Button } from "~/atoms/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/atoms/dropdown-menu";
import { ChevronRight, History } from "lucide-react";
import { type DocumentDetailsVersion } from "~/api/types";
import { FieldStatus } from "~/molecules/FieldStatus";
import { formatDate } from "~/helpers/date";
import { Link, useSearchParams } from "react-router";
import { Badge } from "~/atoms/badge";
import { DocumentVersionStatus } from "~/config/types";
import { useTranslation } from "react-i18next";
import i18n from "~/i18n";
import { cn } from "~/lib/utils";

interface Props {
  documentId: number;
  selectedVersionId: number;
  documentVersions: DocumentDetailsVersion[];
  onVersionChange: () => void;
}

const getStatusDate = (version: DocumentDetailsVersion): string | null => {
  switch (version.status) {
    case DocumentVersionStatus.published:
      return version.publishedAt;
    case DocumentVersionStatus.archived:
      return version.archivedAt;
    default:
      return version.createdAt;
  }
};

const getStatusText = (version: DocumentDetailsVersion): string => {
  const date = getStatusDate(version);
  if (!date) return "";

  switch (version.status) {
    case DocumentVersionStatus.published:
      return `${i18n.t(($) => $.commons.workflow.published)} ${formatDate(date)}`;
    case DocumentVersionStatus.archived:
      return `${i18n.t(($) => $.commons.workflow.archived)} ${formatDate(date)}`;
    default:
      return `${i18n.t(($) => $.commons.workflow.created)} ${formatDate(date)}`;
  }
};

const VersionStatusBadge = ({
  status,
  isWorkflowOngoing,
}: {
  status?: DocumentVersionStatus;
  isWorkflowOngoing?: boolean;
}) => {
  return (
    <div className="flex gap-2">
      {status === DocumentVersionStatus.draft &&
        isWorkflowOngoing === false && (
          <div className="bg-card rounded-full">
            <Badge variant="warn">
              {i18n.t(($) => $.commons.workflow.notStarted)}
            </Badge>
          </div>
        )}
      <div className="bg-card rounded-full">
        <FieldStatus status={status} />
      </div>
    </div>
  );
};

export function VersionSelect({
  documentId,
  selectedVersionId,
  documentVersions,
}: Props) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [isOpen, setIsOpen] = useState(false);

  const sortedVersions = [...documentVersions].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const selectedVersion = sortedVersions.find(
    (version) => version.id === selectedVersionId,
  );

  const selectedVersionNumber = selectedVersion
    ? documentVersions.length -
      sortedVersions.findIndex((v) => v.id === selectedVersion.id)
    : 0;

  const getVersionUrl = (versionId: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("versionId", String(versionId));
    return `/documents/${documentId}?${next.toString()}`;
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <History className="size-4" />
          {t(($) => $.commons.workflow.versionOrder, {
            order: selectedVersionNumber,
          })}
          <VersionStatusBadge
            status={selectedVersion?.status}
            isWorkflowOngoing={selectedVersion?.isWorkflowOngoing}
          />
          <ChevronRight
            className={cn("size-4 transition-transform", isOpen && "rotate-90")}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-card w-87.5" align="end">
        <DropdownMenuLabel>
          {t(($) => $.commons.workflow.versions)}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {sortedVersions.map((version, index) => (
            <DropdownMenuItem
              key={version.id}
              className={version.id === selectedVersionId ? "bg-secondary" : ""}
              asChild
            >
              <Link
                to={getVersionUrl(version.id)}
                className="flex w-full flex-col gap-1"
              >
                <div className="flex w-full items-center justify-between">
                  <span className="font-medium">
                    {t(($) => $.commons.workflow.versionOrder, {
                      order: documentVersions.length - index,
                    })}
                  </span>
                  <VersionStatusBadge
                    status={version.status}
                    isWorkflowOngoing={version.isWorkflowOngoing}
                  />
                </div>
                <div className="text-muted-foreground flex w-full justify-between text-xs">
                  <span>{version.title}</span>
                  <span className="whitespace-nowrap">
                    {getStatusText(version)}
                  </span>
                </div>
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
